import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Stack, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Alert, Skeleton, CircularProgress, LinearProgress,
} from '@mui/material';
import {
  Add, Delete, Edit, KeyboardArrowUp, KeyboardArrowDown,
  Visibility, VisibilityOff, OpenInNew, Image as ImageIcon,
  CloudUpload as UploadIcon, ChangeCircle as ChangeIcon,
} from '@mui/icons-material';
import { auth } from '../../../config/firebase.config';

// ── Types ─────────────────────────────────────────────────────────────────────

interface HeroSlide {
  id: string;
  titulo: string;
  subtitulo?: string;
  descricao?: string;
  imagem_url: string;
  imagem_posicao?: string;
  imagem_objeto?: 'cover' | 'contain';
  link_url?: string;
  link_texto?: string;
  cor_fundo?: string;
  ocultar_titulo?: boolean;
  ocultar_overlay?: boolean;
  ordem: number;
  ativo: number;
  criado_em: string;
}

interface SlideForm {
  titulo: string;
  subtitulo: string;
  descricao: string;
  imagem_url: string;
  imagem_posicao: string;
  imagem_objeto: 'cover' | 'contain';
  link_url: string;
  link_texto: string;
  cor_fundo: string;
  ocultar_titulo: boolean;
  ocultar_overlay: boolean;
  ativo: boolean;
}

const EMPTY_FORM: SlideForm = {
  titulo: '', subtitulo: '', descricao: '',
  imagem_url: '', imagem_posicao: 'center center', imagem_objeto: 'cover',
  link_url: '', link_texto: 'Ver mais',
  cor_fundo: '', ocultar_titulo: false, ocultar_overlay: false, ativo: true,
};

// ── Focal Point Picker ────────────────────────────────────────────────────────

function FocalPointPicker({ imageUrl, value, onChange }: {
  imageUrl: string;
  value: string;
  onChange: (pos: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const parsePos = (pos: string) => {
    const parts = pos.trim().split(/\s+/);
    const parse = (v: string) => {
      if (v === 'left' || v === 'top') return 0;
      if (v === 'center') return 50;
      if (v === 'right' || v === 'bottom') return 100;
      return parseFloat(v) || 50;
    };
    return { x: parse(parts[0] || '50%'), y: parse(parts[1] || '50%') };
  };

  const { x, y } = parsePos(value);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const py = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    onChange(`${px}% ${py}%`);
  };

  const PRESETS = [
    { label: 'Centro', v: '50% 50%' },
    { label: 'Topo', v: '50% 15%' },
    { label: 'Baixo', v: '50% 85%' },
    { label: 'Esquerda', v: '15% 50%' },
    { label: 'Direita', v: '85% 50%' },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Ponto focal da imagem
        </Typography>
        <Typography variant="caption" color="text.disabled" fontFamily="monospace">{value}</Typography>
      </Stack>

      {/* Clickable image */}
      <Box
        ref={containerRef}
        onClick={handleClick}
        sx={{
          position: 'relative', borderRadius: 1.5, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider',
          height: 150, cursor: 'crosshair', userSelect: 'none',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt="focal point"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: value, pointerEvents: 'none', display: 'block' }}
        />
        {/* Crosshair */}
        <Box sx={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
          <Box sx={{ position: 'absolute', top: '50%', left: -12, right: -12, height: '1px', bgcolor: 'white', boxShadow: '0 0 3px rgba(0,0,0,0.8)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: -12, bottom: -12, width: '1px', bgcolor: 'white', boxShadow: '0 0 3px rgba(0,0,0,0.8)' }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid white', boxShadow: '0 0 4px rgba(0,0,0,0.8)' }} />
        </Box>
        <Typography variant="caption" sx={{ position: 'absolute', bottom: 6, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.8)', textShadow: '0 1px 4px rgba(0,0,0,0.8)', pointerEvents: 'none' }}>
          Clica para definir o ponto focal
        </Typography>
      </Box>

      {/* Presets */}
      <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
        {PRESETS.map(p => (
          <Chip
            key={p.v}
            label={p.label}
            size="small"
            variant={value === p.v ? 'filled' : 'outlined'}
            color={value === p.v ? 'primary' : 'default'}
            onClick={() => onChange(p.v)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>
    </Box>
  );
}

// ── Service ───────────────────────────────────────────────────────────────────

const getToken = () => auth.currentUser?.getIdToken(true) ?? Promise.resolve(null);

const useHeroSlidesApi = () => {
  const base = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_VERSION || 'v1'}/admin/hero-slides`;

  const req = async (url: string, init: RequestInit = {}) => {
    const token = await getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(init.headers as any) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { ...init, headers });
    return res.json();
  };

  const list    = ()                                       => req(base);
  const create  = (body: Partial<SlideForm>)               => req(base,          { method: 'POST', body: JSON.stringify(body) });
  const update  = (id: string, body: Partial<SlideForm>)   => req(`${base}/${id}`, { method: 'PUT',  body: JSON.stringify(body) });
  const toggle  = (id: string)                             => req(`${base}/${id}/toggle`, { method: 'PUT' });
  const reorder = (id: string, direction: 'up' | 'down')  => req(`${base}/${id}/ordem`,  { method: 'PUT', body: JSON.stringify({ direction }) });
  const remove  = (id: string)                             => req(`${base}/${id}`,        { method: 'DELETE' });

  return { list, create, update, toggle, reorder, remove };
};

// ── Slide Card ────────────────────────────────────────────────────────────────

function SlideCard({
  slide, index, total, onEdit, onToggle, onReorder, onDelete,
}: {
  slide: HeroSlide; index: number; total: number;
  onEdit: (s: HeroSlide) => void;
  onToggle: (s: HeroSlide) => void;
  onReorder: (s: HeroSlide, dir: 'up' | 'down') => void;
  onDelete: (s: HeroSlide) => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: 1.5,
        opacity: slide.ativo ? 1 : 0.55,
        borderLeft: slide.ativo ? '4px solid' : '4px solid transparent',
        borderLeftColor: slide.ativo ? 'primary.main' : 'transparent',
        transition: 'opacity .2s',
      }}
    >
      {/* Thumb */}
      <Box
        sx={{
          width: 120, height: 68, borderRadius: 1, overflow: 'hidden', flexShrink: 0,
          bgcolor: slide.cor_fundo || 'action.hover',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {slide.imagem_url ? (
          <img
            src={slide.imagem_url}
            alt={slide.titulo}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <ImageIcon sx={{ color: 'grey.400' }} />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap fontWeight={600}>{slide.titulo}</Typography>
        {slide.subtitulo && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">{slide.subtitulo}</Typography>
        )}
        {slide.link_url && (
          <Typography variant="caption" color="primary" noWrap display="block">
            {slide.link_texto || 'Ver mais'} → {slide.link_url.substring(0, 50)}{slide.link_url.length > 50 ? '…' : ''}
          </Typography>
        )}
      </Box>

      {/* Status */}
      <Chip
        size="small"
        label={slide.ativo ? 'Activo' : 'Inactivo'}
        color={slide.ativo ? 'success' : 'default'}
        variant="outlined"
        sx={{ flexShrink: 0 }}
      />

      {/* Actions */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Tooltip title="Mover para cima">
          <span>
            <IconButton size="small" disabled={index === 0} onClick={() => onReorder(slide, 'up')}>
              <KeyboardArrowUp fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Mover para baixo">
          <span>
            <IconButton size="small" disabled={index === total - 1} onClick={() => onReorder(slide, 'down')}>
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={slide.ativo ? 'Desactivar' : 'Activar'}>
          <IconButton size="small" onClick={() => onToggle(slide)}>
            {slide.ativo ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit(slide)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        {slide.imagem_url && (
          <Tooltip title="Abrir imagem">
            <IconButton size="small" component="a" href={slide.imagem_url} target="_blank">
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Eliminar">
          <IconButton size="small" color="error" onClick={() => onDelete(slide)}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

// ── Slide Form Dialog ─────────────────────────────────────────────────────────

function SlideDialog({
  open, slide, saving, onClose, onSave,
}: {
  open: boolean; slide: HeroSlide | null; saving: boolean;
  onClose: () => void;
  onSave: (form: SlideForm) => void;
}) {
  const [form, setForm]           = useState<SlideForm>(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const set = (k: keyof SlideForm, v: any) => setForm(f => ({ ...f, [k]: v }));
  const uploadBase = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_VERSION || 'v1'}/admin/hero-slides/upload`;

  useEffect(() => {
    if (open) {
      setUploadError('');
      setForm(slide ? {
        titulo:          slide.titulo,
        subtitulo:       slide.subtitulo      || '',
        descricao:       slide.descricao      || '',
        imagem_url:      slide.imagem_url     || '',
        imagem_posicao:  slide.imagem_posicao || 'center center',
        imagem_objeto:   (slide.imagem_objeto === 'contain' ? 'contain' : 'cover') as 'cover' | 'contain',
        link_url:        slide.link_url       || '',
        link_texto:      slide.link_texto     || 'Ver mais',
        cor_fundo:       slide.cor_fundo      || '',
        ocultar_titulo:  Boolean(slide.ocultar_titulo),
        ocultar_overlay: Boolean(slide.ocultar_overlay),
        ativo:           Boolean(slide.ativo),
      } : EMPTY_FORM);
    }
  }, [open, slide]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(uploadBase, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const json = await res.json();
      if (json.success) {
        set('imagem_url', json.data.url);
      } else {
        setUploadError(json.error || 'Erro no upload');
      }
    } catch {
      setUploadError('Erro de ligação');
    } finally {
      setUploading(false);
      // Reset file input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const valid = form.titulo.trim() && form.imagem_url.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{slide ? 'Editar Slide' : 'Novo Slide'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Título *" fullWidth size="small"
            value={form.titulo} onChange={e => set('titulo', e.target.value)}
          />
          <TextField
            label="Subtítulo" fullWidth size="small"
            value={form.subtitulo} onChange={e => set('subtitulo', e.target.value)}
          />
          <TextField
            label="Descrição" fullWidth size="small" multiline rows={2}
            value={form.descricao} onChange={e => set('descricao', e.target.value)}
          />

          {/* Image upload */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Imagem *
            </Typography>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {form.imagem_url ? (
              /* Existing or newly uploaded image */
              <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Box
                  component="img"
                  src={form.imagem_url}
                  alt="preview"
                  sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block', bgcolor: form.cor_fundo || 'action.hover' }}
                  onError={(e: any) => { e.target.style.opacity = '0.3'; }}
                />
                <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <ChangeIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    sx={{ backdropFilter: 'blur(4px)', bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                  >
                    {uploading ? 'A enviar…' : 'Alterar imagem'}
                  </Button>
                </Box>
              </Box>
            ) : (
              /* No image yet — upload area */
              <Box
                onClick={() => !uploading && fileInputRef.current?.click()}
                sx={{
                  height: 140, borderRadius: 1.5, border: '2px dashed',
                  borderColor: uploading ? 'primary.main' : 'divider',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 1, cursor: uploading ? 'wait' : 'pointer',
                  transition: 'border-color .2s, background .2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                {uploading ? (
                  <>
                    <CircularProgress size={28} />
                    <Typography variant="caption" color="text.secondary">A enviar imagem…</Typography>
                    <LinearProgress sx={{ width: '60%', borderRadius: 1 }} />
                  </>
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary">Clica para selecionar imagem</Typography>
                    <Typography variant="caption" color="text.disabled">JPG, PNG, WebP · máx. 8 MB · recomendado 1920×1080</Typography>
                  </>
                )}
              </Box>
            )}

            {uploadError && (
              <Typography variant="caption" color="error" display="block" mt={0.5}>{uploadError}</Typography>
            )}
          </Box>

          {/* Modo de exibição */}
          {form.imagem_url && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                Modo de exibição
              </Typography>
              <Stack direction="row" spacing={1}>
                {([['cover', 'Cobrir', 'Preenche o hero, corta os lados'], ['contain', 'Conter', 'Mostra a imagem inteira — ideal para banners']] as const).map(([v, label, hint]) => (
                  <Chip
                    key={v}
                    label={label}
                    title={hint}
                    size="small"
                    variant={form.imagem_objeto === v ? 'filled' : 'outlined'}
                    color={form.imagem_objeto === v ? 'primary' : 'default'}
                    onClick={() => set('imagem_objeto', v)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                {form.imagem_objeto === 'contain'
                  ? 'A imagem inteira é visível — ideal para banners com texto embutido.'
                  : 'A imagem preenche todo o hero — ideal para fotos de eventos.'}
              </Typography>
            </Box>
          )}

          {/* Focal point — only in cover mode */}
          {form.imagem_url && form.imagem_objeto !== 'contain' && (
            <FocalPointPicker
              imageUrl={form.imagem_url}
              value={form.imagem_posicao}
              onChange={v => set('imagem_posicao', v)}
            />
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="URL do Link" fullWidth size="small"
              placeholder="https://..."
              value={form.link_url} onChange={e => set('link_url', e.target.value)}
            />
            <TextField
              label="Texto do botão" size="small" sx={{ width: 160, flexShrink: 0 }}
              value={form.link_texto} onChange={e => set('link_texto', e.target.value)}
            />
          </Stack>

          <TextField
            label="Cor de fundo (fallback)" size="small" sx={{ width: 200 }}
            placeholder="#1a1a2e"
            value={form.cor_fundo} onChange={e => set('cor_fundo', e.target.value)}
            helperText="Exibida se a imagem não carregar"
          />

          <FormControlLabel
            control={<Switch checked={form.ocultar_titulo} onChange={e => set('ocultar_titulo', e.target.checked)} />}
            label="Ocultar título no slider"
          />
          <FormControlLabel
            control={<Switch checked={form.ocultar_overlay} onChange={e => set('ocultar_overlay', e.target.checked)} />}
            label="Sem overlay (imagem a 100%)"
          />

          <FormControlLabel
            control={<Switch checked={form.ativo} onChange={e => set('ativo', e.target.checked)} />}
            label="Slide activo"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || uploading}>Cancelar</Button>
        <Button
          variant="contained" disabled={!valid || saving || uploading}
          onClick={() => onSave(form)}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'A guardar…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function HeroSlides() {
  const [slides, setSlides]         = useState<HeroSlide[]>([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSlide, setEditSlide]   = useState<HeroSlide | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HeroSlide | null>(null);

  const api = useHeroSlidesApi();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.list();
      if (res.success) setSlides(res.data || []);
      else setError(res.error || 'Erro ao carregar slides');
    } catch { setError('Erro de ligação'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (form: SlideForm) => {
    setSaving(true);
    try {
      const res = editSlide
        ? await api.update(editSlide.id, form)
        : await api.create(form);
      if (res.success) { setDialogOpen(false); load(); }
      else setError(res.error || 'Erro ao guardar');
    } catch { setError('Erro de ligação'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (slide: HeroSlide) => {
    await api.toggle(slide.id);
    load();
  };

  const handleReorder = async (slide: HeroSlide, dir: 'up' | 'down') => {
    await api.reorder(slide.id, dir);
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.remove(deleteTarget.id);
    setDeleteTarget(null);
    load();
  };

  const openNew  = () => { setEditSlide(null); setDialogOpen(true); };
  const openEdit = (s: HeroSlide) => { setEditSlide(s); setDialogOpen(true); };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Hero Slides — Events.cv</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Gerir as imagens e mensagens do slider no topo da homepage de events.cv.
            A ordem aqui reflecte-se directamente no site.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openNew} sx={{ flexShrink: 0 }}>
          Novo Slide
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Preview link */}
      <Alert severity="info" sx={{ mb: 3 }} icon={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption">
            Os slides activos aparecem na homepage de{' '}
            <strong>events.cv</strong>. Mínimo recomendado: 3 slides.
          </Typography>
          <Button
            size="small" endIcon={<OpenInNew />}
            component="a" href="https://events.cv" target="_blank"
          >
            Ver site
          </Button>
        </Stack>
      </Alert>

      {/* Slides list */}
      {loading ? (
        <Stack spacing={1.5}>
          {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rectangular" height={84} sx={{ borderRadius: 1 }} />)}
        </Stack>
      ) : slides.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Nenhum slide criado ainda.</Typography>
          <Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }} onClick={openNew}>
            Criar primeiro slide
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {slides.map((s, i) => (
            <SlideCard
              key={s.id}
              slide={s}
              index={i}
              total={slides.length}
              onEdit={openEdit}
              onToggle={handleToggle}
              onReorder={handleReorder}
              onDelete={setDeleteTarget}
            />
          ))}
        </Stack>
      )}

      {/* Form dialog */}
      <SlideDialog
        open={dialogOpen}
        slide={editSlide}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar slide</DialogTitle>
        <DialogContent>
          <Typography>
            Tens a certeza que queres eliminar o slide <strong>"{deleteTarget?.titulo}"</strong>?
            Esta acção é irreversível.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
